/**
 * AI Explanation Formatting Utilities
 * Provides functions for formatting and displaying AI reasoning and explanations
 */
/**
 * Format recommendation explanation for display
 */
export function formatRecommendationExplanation(explanation, options = {}) {
    const { maxLength = 200, showFactors = true, showAlternatives = false, } = options;
    let primary = explanation.primaryReason;
    // Truncate if needed
    if (primary.length > maxLength) {
        primary = primary.substring(0, maxLength - 3) + '...';
    }
    const factors = [];
    if (showFactors) {
        // Add skill alignment factors
        if (explanation.skillAlignment.length > 0) {
            const topSkills = explanation.skillAlignment
                .filter(skill => skill.alignmentScore > 0.7)
                .slice(0, 3)
                .map(skill => skill.skillName);
            if (topSkills.length > 0) {
                factors.push(`Matches your ${topSkills.join(', ')} skills`);
            }
        }
        // Add difficulty fit
        if (explanation.difficultyFit > 0.8) {
            factors.push('Perfect difficulty level for you');
        }
        else if (explanation.difficultyFit > 0.6) {
            factors.push('Good difficulty match');
        }
        // Add personalized factors
        factors.push(...explanation.personalizedFactors.slice(0, 3));
    }
    const result = {
        primary,
        factors,
        confidence: explanation.confidence,
    };
    if (showAlternatives) {
        result.alternatives = explanation.alternativeOptions;
    }
    return result;
}
/**
 * Format skill alignment explanation
 */
export function formatSkillAlignmentExplanation(alignments) {
    if (alignments.length === 0) {
        return 'No specific skill alignment identified.';
    }
    const strongAlignments = alignments.filter(a => a.alignmentScore > 0.7);
    const gapFillers = alignments.filter(a => a.gapFilled);
    if (strongAlignments.length > 0) {
        const skillNames = strongAlignments.slice(0, 3).map(a => a.skillName);
        return `Strongly aligns with your ${skillNames.join(', ')} skills.`;
    }
    if (gapFillers.length > 0) {
        const gapSkills = gapFillers.slice(0, 2).map(a => a.skillName);
        return `Helps fill gaps in ${gapSkills.join(' and ')} skills.`;
    }
    return 'Provides general skill development opportunities.';
}
/**
 * Format adaptation suggestion explanation
 */
export function formatAdaptationExplanation(suggestion) {
    const { reasoning, impact } = suggestion;
    let explanation = reasoning;
    // Add impact information
    const impactParts = [];
    if (impact.engagementChange > 0.1) {
        impactParts.push('increase engagement');
    }
    if (impact.performanceChange > 0.1) {
        impactParts.push('improve performance');
    }
    if (impact.satisfactionChange > 0.1) {
        impactParts.push('boost satisfaction');
    }
    if (impactParts.length > 0) {
        explanation += ` This change is expected to ${impactParts.join(' and ')}.`;
    }
    return explanation;
}
/**
 * Format progress insight explanation
 */
export function formatProgressInsightExplanation(insight) {
    const { description, trend, change, aiGenerated } = insight;
    let explanation = description;
    // Add trend information
    if (trend === 'up' && change > 0) {
        explanation += ` You've improved by ${Math.round(change * 100)}%.`;
    }
    else if (trend === 'down' && change < 0) {
        explanation += ` There's been a ${Math.round(Math.abs(change) * 100)}% decrease.`;
    }
    // Add AI attribution if generated
    if (aiGenerated) {
        explanation += ' (AI-generated insight)';
    }
    return explanation;
}
/**
 * Generate contextual explanation based on user behavior
 */
export function generateContextualExplanation(context, template) {
    let explanation = template;
    // Replace placeholders with context values
    Object.entries(context).forEach(([key, value]) => {
        const placeholder = `{${key}}`;
        if (explanation.includes(placeholder)) {
            explanation = explanation.replace(placeholder, String(value));
        }
    });
    return explanation;
}
/**
 * Format explanation for accessibility
 */
export function formatExplanationForA11y(explanation) {
    let a11yText = `AI explanation: ${explanation.primary}`;
    if (explanation.factors.length > 0) {
        a11yText += ` Key factors: ${explanation.factors.join(', ')}.`;
    }
    a11yText += ` Confidence level: ${Math.round(explanation.confidence * 100)}%.`;
    return a11yText;
}
/**
 * Simplify explanation for different reading levels
 */
export function simplifyExplanation(explanation, level = 'intermediate') {
    if (level === 'basic') {
        // Remove technical terms and complex sentences
        return explanation
            .replace(/algorithm|optimization|correlation/gi, 'system')
            .replace(/personalization|customization/gi, 'personal settings')
            .replace(/confidence|probability/gi, 'certainty')
            .split('. ')
            .slice(0, 2) // Keep only first 2 sentences
            .join('. ');
    }
    if (level === 'advanced') {
        // Keep full technical explanation
        return explanation;
    }
    // Intermediate level - moderate simplification
    return explanation
        .replace(/machine learning algorithm/gi, 'AI system')
        .replace(/statistical correlation/gi, 'pattern matching');
}
/**
 * Generate explanation summary
 */
export function generateExplanationSummary(explanation, maxWords = 20) {
    const words = explanation.primary.split(' ');
    if (words.length <= maxWords) {
        return explanation.primary;
    }
    // Take first part and add key factors
    const truncated = words.slice(0, maxWords - 3).join(' ');
    const topFactor = explanation.factors[0];
    if (topFactor) {
        return `${truncated}... (${topFactor})`;
    }
    return `${truncated}...`;
}
/**
 * Format explanation with confidence indicator
 */
export function formatExplanationWithConfidence(explanation, confidence) {
    const confidenceText = confidence > 0.8
        ? 'High confidence'
        : confidence > 0.6
            ? 'Medium confidence'
            : confidence > 0.4
                ? 'Low confidence'
                : 'Very low confidence';
    return `${explanation} (${confidenceText}: ${Math.round(confidence * 100)}%)`;
}
/**
 * Generate explanation for different user types
 */
export function generatePersonalizedExplanation(baseExplanation, userType, context) {
    let explanation = baseExplanation;
    switch (userType) {
        case 'beginner':
            explanation = `As you're starting your coffee journey, ${explanation.toLowerCase()}`;
            if (context?.['supportiveMessage']) {
                explanation += ` ${context['supportiveMessage']}`;
            }
            break;
        case 'intermediate':
            explanation = `Based on your growing expertise, ${explanation.toLowerCase()}`;
            if (context?.['skillFocus']) {
                explanation += ` This will help develop your ${context['skillFocus']} skills.`;
            }
            break;
        case 'expert':
            explanation = `Given your advanced skills, ${explanation.toLowerCase()}`;
            if (context?.['technicalDetails']) {
                explanation += ` ${context['technicalDetails']}`;
            }
            break;
    }
    return explanation;
}
/**
 * Validate explanation quality
 */
export function validateExplanationQuality(explanation) {
    const issues = [];
    let score = 1.0;
    // Check length
    if (explanation.length < 10) {
        issues.push('Explanation too short');
        score -= 0.3;
    }
    if (explanation.length > 500) {
        issues.push('Explanation too long');
        score -= 0.2;
    }
    // Check for vague language
    const vagueTerms = ['maybe', 'possibly', 'might', 'could be', 'perhaps'];
    const vagueCount = vagueTerms.filter(term => explanation.toLowerCase().includes(term)).length;
    if (vagueCount > 2) {
        issues.push('Too many vague terms');
        score -= 0.2;
    }
    // Check for specific information
    const specificTerms = ['because', 'due to', 'based on', 'since', 'given'];
    const hasSpecifics = specificTerms.some(term => explanation.toLowerCase().includes(term));
    if (!hasSpecifics) {
        issues.push('Lacks specific reasoning');
        score -= 0.3;
    }
    return {
        isValid: issues.length === 0,
        issues,
        score: Math.max(0, score),
    };
}
